import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { Service, ROUTE_PATHS } from "@/lib/index";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * @description Carte de présentation d'un service spécifique (Vin, Résidence, Terrain)
 */
export function ServiceCard({ service }: { service: Service }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      whileHover={{ y: -8 }}
      className="group h-full"
    >
      <Card className="h-full overflow-hidden border-border/40 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={IMAGES[service.imageKey]}
            alt={service.title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
          <div className="absolute bottom-4 left-4">
            <span className="inline-block rounded-full bg-primary/90 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
              {service.category === "wine" ? "Prestige" : service.category === "residence" ? "Confort" : "Investissement"}
            </span>
          </div>
        </div>

        <CardHeader className="space-y-1">
          <CardTitle className="font-serif text-2xl tracking-tight text-foreground">
            {service.title}
          </CardTitle>
          <CardDescription className="line-clamp-2 text-muted-foreground">
            {service.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-grow">
          <ul className="space-y-2">
            {service.features.slice(0, 3).map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>

        <CardFooter className="pt-4">
          <Button
            asChild
            variant="outline"
            className="w-full border-primary/20 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to={`${ROUTE_PATHS.SERVICES}#${service.id}`}>
              En savoir plus
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

/**
 * @description Carte de caractéristique mettant en avant un avantage de l'entreprise
 */
export function FeatureCard({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="group relative h-full rounded-2xl border border-border/50 bg-gradient-to-br from-card to-background p-8 transition-all hover:border-primary/30"
    >
      <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground">
        {icon}
      </div>
      
      <h3 className="mb-3 font-serif text-xl font-semibold tracking-tight text-foreground">
        {title}
      </h3>
      
      <p className="text-sm leading-relaxed text-muted-foreground">
        {description}
      </p>

      <div className="absolute bottom-0 left-0 h-1 w-0 bg-primary transition-all duration-500 group-hover:w-full" />
    </motion.div>
  );
}
